import {recordAccountBlockResponse} from './accountAccessState';

export interface AuthRuntimeAdapter {
    getToken: () => string | undefined;
    refreshToken: () => Promise<boolean>;
    onSessionExpired: () => void | Promise<void>;
    isTokenExpiringWithin?: (seconds: number) => boolean;
    isSessionEnded?: (error: unknown) => boolean;
}

export const PRE_REQUEST_REFRESH_WINDOW_SECONDS = 5;

export const REFRESH_TIMEOUT_MS = 15_000;
export const MAX_CONSECUTIVE_REFRESH_FAILURES = 3;
export const MIN_TIMEOUT_STREAK_MS = 60_000;

export class RefreshTimeoutError extends Error {
    constructor() {
        super('Token refresh timed out');
        this.name = 'RefreshTimeoutError';
    }
}

export class SessionEndedError extends Error {
    constructor(options?: ErrorOptions) {
        super('Authentication session ended', options);
        this.name = 'SessionEndedError';
    }
}

const isOnline = (): boolean => typeof navigator === 'undefined' || navigator.onLine !== false;

const isTabVisible = (): boolean => typeof document === 'undefined' || document.visibilityState === 'visible';

const withRefreshTimeout = (attempt: Promise<boolean>): Promise<boolean> =>
    new Promise<boolean>((resolve, reject) => {
        const timer = setTimeout(() => reject(new RefreshTimeoutError()), REFRESH_TIMEOUT_MS);
        attempt.then(
            (value) => {
                clearTimeout(timer);
                resolve(value);
            },
            (error) => {
                clearTimeout(timer);
                reject(error);
            },
        );
    });

let adapter: AuthRuntimeAdapter | null = null;
let refreshInFlight: Promise<boolean> | null = null;
let sessionExpiryTriggered = false;
let consecutiveRefreshTimeouts = 0;
let firstTimeoutAt = 0;
const tokenChangeListeners = new Set<() => void>();

export const configureAuthRuntime = (nextAdapter: AuthRuntimeAdapter): void => {
    adapter = nextAdapter;
    refreshInFlight = null;
    sessionExpiryTriggered = false;
    consecutiveRefreshTimeouts = 0;
};

export const getAccessToken = (): string | undefined => adapter?.getToken();

export const notifyAccessTokenChanged = (): void => {
    tokenChangeListeners.forEach((listener) => listener());
};

export const subscribeToAccessTokenChanges = (listener: () => void): (() => void) => {
    tokenChangeListeners.add(listener);
    return () => tokenChangeListeners.delete(listener);
};

export const refreshAccessToken = async (): Promise<boolean> => {
    if (!adapter) {
        return false;
    }

    if (!refreshInFlight) {
        const currentAdapter = adapter;
        const attempt = Promise.resolve().then(() => currentAdapter.refreshToken());
        const inFlight: Promise<boolean> = withRefreshTimeout(attempt)
            .then((refreshed) => {
                consecutiveRefreshTimeouts = 0;
                if (refreshed) {
                    sessionExpiryTriggered = false;
                }
                return refreshed;
            })
            .catch(async (error) => {
                if (error instanceof RefreshTimeoutError) {
                    if (!isOnline() || !isTabVisible()) {
                        consecutiveRefreshTimeouts = 0;
                        return false;
                    }
                    if (consecutiveRefreshTimeouts === 0) {
                        firstTimeoutAt = Date.now();
                    }
                    consecutiveRefreshTimeouts += 1;
                    if (
                        consecutiveRefreshTimeouts >= MAX_CONSECUTIVE_REFRESH_FAILURES &&
                        Date.now() - firstTimeoutAt >= MIN_TIMEOUT_STREAK_MS
                    ) {
                        consecutiveRefreshTimeouts = 0;
                        await expireSession();
                        throw new SessionEndedError({cause: error});
                    }
                    return false;
                }
                consecutiveRefreshTimeouts = 0;
                if (currentAdapter.isSessionEnded?.(error)) {
                    await expireSession();
                    throw new SessionEndedError({cause: error});
                }
                throw error;
            })
            .finally(() => {
                if (refreshInFlight === inFlight) {
                    refreshInFlight = null;
                }
            });
        refreshInFlight = inFlight;
    }

    return refreshInFlight;
};

export const isAccessTokenExpiringWithin = (seconds: number): boolean => {
    try {
        return adapter?.isTokenExpiringWithin?.(seconds) ?? false;
    } catch {
        return false;
    }
};

export const ensureFreshAccessToken = async (): Promise<void> => {
    if (!isAccessTokenExpiringWithin(PRE_REQUEST_REFRESH_WINDOW_SECONDS)) {
        return;
    }
    try {
        await refreshAccessToken();
    } catch {
        return;
    }
};

export const expireSession = async (): Promise<void> => {
    if (!adapter || sessionExpiryTriggered) {
        return;
    }

    sessionExpiryTriggered = true;
    await adapter.onSessionExpired();
};

/**
 * Shared "execute → on 401 refresh if needed → retry if refreshed" core.
 * Used by both the RTK Query base query and authenticatedFetch so the retry
 * semantics live in one place. `isUnauthorized` adapts to each result shape
 * (a Response vs an RTK QueryReturnValue).
 */
export const runWithAuthRetry = async <T>({
    execute,
    isUnauthorized,
    refresh,
    onSessionExpired,
}: {
    execute: () => Promise<T>;
    isUnauthorized: (result: T) => boolean;
    refresh: () => Promise<boolean>;
    onSessionExpired: () => Promise<void>;
}): Promise<T> => {
    const result = await execute();
    if (!isUnauthorized(result)) {
        return result;
    }

    try {
        const refreshed = await refresh();

        // Keycloak updateToken(minValidity) returns false when the current
        // access token is still valid. In that case the 401 belongs to the
        // requested resource and must not trigger a login redirect.
        if (!refreshed) {
            return result;
        }
    } catch (error) {
        if (error instanceof SessionEndedError) {
            await onSessionExpired();
        }
        return result;
    }

    // Even after a successful refresh the resource may legitimately reject
    // the request. Return that response without redirecting through the same
    // valid Keycloak SSO session and creating a redirect loop.
    return execute();
};

export const authenticatedFetch = async (
    input: string | URL,
    init: RequestInit = {},
): Promise<Response> => {
    const execute = async (): Promise<Response> => {
        await ensureFreshAccessToken();
        const token = getAccessToken();
        if (!token) {
            throw new Error('Authentication is not ready');
        }

        const headers = new Headers(init.headers);
        headers.set('Authorization', `Bearer ${token}`);
        return fetch(input, {...init, headers});
    };

    const response = await runWithAuthRetry({
        execute,
        isUnauthorized: (result) => result.status === 401,
        refresh: refreshAccessToken,
        onSessionExpired: expireSession,
    });
    await recordAccountBlockResponse(response);
    return response;
};

// Test-only reset kept out of production call sites.
export const resetAuthRuntimeForTests = (): void => {
    adapter = null;
    refreshInFlight = null;
    sessionExpiryTriggered = false;
    consecutiveRefreshTimeouts = 0;
    tokenChangeListeners.clear();
};
